import React from "react";
import TourStep from "./TourStep";
import SbTourMessage from "./SbTourMessage";

/**
 * buildTourSteps — turn a module's step list into the AntD Tour `steps` array
 * plus the matching `actionsRender` / `indicatorsRender`.
 *
 * Keyed steps become a TourStep spotlight targeting `resolveTarget(step)`; steps
 * without a key (Welcome / Finish) become a centered SbTourMessage greeting whose
 * buttons come from `greeting`. Greeting steps hide AntD's Prev/Next + counter
 * (their buttons live inside SbTourMessage).
 *
 *   const { steps, actionsRender, indicatorsRender } = buildTourSteps(tourSteps, {
 *     resolveTarget: (step) => refs[step.key]?.current,
 *     greeting: {
 *       icon: (step, isLast) => (isLast ? <CheckIcon /> : <ModuleIcon />),
 *       onPrimary: (step, index, isLast) => (isLast ? finish() : goToStep(index + 1)),
 *       onSecondary: () => close(),
 *     },
 *   });
 *
 * @param {Array}    steps    Module steps ({ key?, title, description, placement,
 *                            primaryText?, secondaryText? }).
 * @param {Function} resolveTarget  (step) => DOM element for a keyed step.
 * @param {Object}   greeting       { icon, onPrimary, onSecondary } for greetings.
 */
export default function buildTourSteps(steps, { resolveTarget, greeting }) {
  const lastIndex = steps.length - 1;

  const built = steps.map((step, index) =>
    step.key
      ? {
          description: <TourStep title={step.title} description={step.description} />,
          placement: step.placement,
          target: () => resolveTarget(step),
        }
      : {
          description: (
            <SbTourMessage
              icon={greeting.icon(step, index === lastIndex)}
              title={step.title}
              description={step.description}
              primaryText={step.primaryText}
              secondaryText={step.secondaryText}
              onPrimary={() => greeting.onPrimary(step, index, index === lastIndex)}
              onSecondary={() => greeting.onSecondary(step)}
            />
          ),
        },
  );

  const actionsRender = (originNode, { current }) =>
    steps[current]?.key ? originNode : null;
  const indicatorsRender = (current, total) =>
    steps[current]?.key ? `${current + 1} / ${total}` : null;

  return { steps: built, actionsRender, indicatorsRender };
}
